import React, { useEffect, useState } from 'react';
import { calcularRFC } from '../utils/calculoRfcCurp';

// Banregio · Obligado Solidario (mig 114). Va en BancoBanregio.jsx antes de "Información del Crédito". El valor vive en
// br.obligado_solidario y viaja en el mismo POST /api/banregio/:id (utils/obligadoBanregio.js lo valida y guarda).
// Por defecto No. Con "No" la sección se pliega pero lo capturado se conserva (se guarda con incluye=false y no se imprime).
// Estado civil con el catálogo de Banregio del titular; colonia, ciudad y entidad por SEPOMEX, igual que en Banorte.
// Si el obligado es Casado Sociedad Conyugal se captura a su cónyuge (sin domicilio: se asume el mismo del obligado; su celular
// es opcional y se imprime en su Buró, en blanco si no se captura).
const API = '/api';
const RFC_VALIDO = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/;
const SOCIEDAD_CONYUGAL = 'Casado Sociedad Conyugal';
// Ancho mínimo legible (px), medido con la letra de la pantalla: fecha dd/mm/aaaa con su ícono, RFC de 13 con el botón Calcular,
// "Casado Separación de Bienes", teléfono de 10 dígitos, CP de 5 y un número exterior/interior corto.
// RFC 288 = 240 × 1.2 (Marco pidió el RFC 20% más ancho, 2026-10-28).
const ANCHO_MIN = { fecha: 140, rfc: 288, estadoCivil: 250, calle: 160, numero: 80, cp: 90, telefono: 120 };
const FALTANTES_OBLIGADO = [['nombres', 'Nombre(s)'], ['apellido_paterno', 'Apellido Paterno'], ['fecha_nacimiento', 'Fecha de Nacimiento'],
  ['rfc', 'RFC'], ['estado_civil_id', 'Estado Civil'], ['calle', 'Calle'], ['no_ext', 'No. Ext.'], ['codigo_postal', 'Código Postal'],
  ['colonia', 'Colonia'], ['telefono_celular', 'Teléfono Celular']];
const FALTANTES_CONYUGE = [['conyuge_nombres', 'Nombre(s)'], ['conyuge_apellido_paterno', 'Apellido Paterno'],
  ['conyuge_fecha_nacimiento', 'Fecha de Nacimiento'], ['conyuge_rfc', 'RFC']];

// Lo que impide guardar (mismas reglas que el servidor con incluye = Sí). Vacío nunca impide.
export function erroresObligadoBanregio(o) {
  if (!o || !o.incluye) return [];
  const e = [];
  if (o.codigo_postal && !/^\d{5}$/.test(o.codigo_postal)) e.push('El código postal del obligado solidario debe tener 5 dígitos.');
  if (o.rfc && String(o.rfc).length > 13) e.push('El RFC del obligado solidario admite hasta 13 caracteres.');
  if (o.conyuge_rfc && String(o.conyuge_rfc).length > 13) e.push('El RFC del cónyuge del obligado admite hasta 13 caracteres.');
  return e;
}

export default function ObligadoSolidarioBanregio({ valor, onChange, estadosCiviles, st, isMobile, isTablet }) {
  const o = valor || {};
  const [colonias, setColonias] = useState([]);
  const [avisoCp, setAvisoCp] = useState('');
  const set = (k, v) => onChange({ ...o, [k]: v });
  const setVarios = (cambios) => onChange({ ...o, ...cambios });
  const cols = (n) => ({ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : `repeat(${n}, 1fr)`, gap: 16, marginBottom: 4 });
  const soloLectura = { ...st.input, backgroundColor: '#f3f4f6' };
  // Renglones con ancho propio por campo (Marco, 2026-10-28): pct = % del ancho que el campo tenía en la cuadrícula de n columnas;
  // juntos y a la izquierda. `min` (px) evita que se corte lo capturado; si no caben, bajan al siguiente renglón. Celular: 100%.
  const fila = { display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 4, alignItems: 'flex-start' };
  const ancho = (n, pct, min) => {
    if (isMobile) return { width: '100%' };
    const c = isTablet ? 2 : n;
    return { flex: '0 0 auto', width: `max(${min}px, calc((100% - ${(c - 1) * 16}px) / ${c} * ${pct / 100}))`, maxWidth: '100%', boxSizing: 'border-box' };
  };

  // Colonias del código postal ya guardado (al abrir el prospecto).
  useEffect(() => {
    if (!o.incluye || !/^\d{5}$/.test(o.codigo_postal || '') || colonias.length) return;
    fetch(`${API}/sepomex/cp/${o.codigo_postal}`).then(r => r.json()).then(d => setColonias(Array.isArray(d) ? d : [])).catch(() => {});
  }, [o.incluye, o.codigo_postal]); // eslint-disable-line react-hooks/exhaustive-deps

  const buscarCp = async (cp) => {
    setAvisoCp(''); setColonias([]);
    if (!/^\d{5}$/.test(cp)) return;
    try {
      const d = await (await fetch(`${API}/sepomex/cp/${cp}`)).json();
      if (Array.isArray(d) && d.length) {
        setColonias(d);
        setVarios({ codigo_postal: cp, ciudad: d[0].municipio || d[0].ciudad || '', entidad_federativa: d[0].estado || '',
          colonia: d.some(x => x.colonia === o.colonia) ? o.colonia : (d.length === 1 ? d[0].colonia : '') });
      } else setAvisoCp('Código postal no encontrado en SEPOMEX');
    } catch { setAvisoCp('No se pudo consultar el código postal'); }
  };
  // RFC con el mismo cálculo que Banorte; prefijo '' = obligado, 'conyuge_' = su cónyuge.
  const calcular = (prefijo) => {
    const p = { nombres: o[prefijo + 'nombres'], apellido_paterno: o[prefijo + 'apellido_paterno'],
      apellido_materno: o[prefijo + 'apellido_materno'], fecha_nacimiento: o[prefijo + 'fecha_nacimiento'] };
    if (!p.nombres || !(p.apellido_paterno || p.apellido_materno) || !p.fecha_nacimiento) {
      alert(`Para calcular el RFC, primero captura nombre(s), un apellido y fecha de nacimiento ${prefijo ? 'del cónyuge del obligado' : 'del obligado solidario'}.`);
      return;
    }
    set(prefijo + 'rfc', calcularRFC(p));
  };

  const esSociedadConyugal = (estadosCiviles.find(e => String(e.id) === String(o.estado_civil_id)) || {}).nombre === SOCIEDAD_CONYUGAL;
  const avisos = [];
  if (o.incluye) {
    // Lista de lo que falta (Marco, 2026-10-28). Solo informa: nunca impide guardar. Opcionales: Apellido Materno, No. Int. y el
    // celular del cónyuge; Ciudad y Entidad salen del código postal. Los formatos se imprimen con nombre(s) y un apellido.
    const faltan = (campos) => campos.filter(([k]) => o[k] === undefined || o[k] === null || String(o[k]).trim() === '').map(([, t]) => t);
    const fo = faltan(FALTANTES_OBLIGADO);
    if (fo.length) avisos.push(`Faltan del obligado solidario: ${fo.join(', ')}.`);
    const fc = esSociedadConyugal ? faltan(FALTANTES_CONYUGE) : [];
    if (fc.length) avisos.push(`Faltan del cónyuge: ${fc.join(', ')}.`);
    if (o.rfc && !RFC_VALIDO.test(o.rfc)) avisos.push('El RFC del obligado no tiene el formato esperado (12 o 13 caracteres).');
    if (o.telefono_celular && String(o.telefono_celular).length !== 10) avisos.push('El teléfono celular debe tener 10 dígitos.');
    if (esSociedadConyugal) {
      if (o.conyuge_rfc && !RFC_VALIDO.test(o.conyuge_rfc)) avisos.push('El RFC del cónyuge no tiene el formato esperado (12 o 13 caracteres).');
      if (o.conyuge_telefono_celular && String(o.conyuge_telefono_celular).length !== 10) avisos.push('El teléfono celular del cónyuge debe tener 10 dígitos.');
    }
  }
  const campo = (k, etiqueta, props = {}) => (
    <div style={{ ...st.field, ...props.estilo }}>
      <label style={st.label}>{etiqueta}</label>
      <input value={o[k] ?? ''} onChange={e => set(k, props.digitos ? e.target.value.replace(/\D/g, '') : e.target.value)}
        maxLength={props.max} type={props.type || 'text'} inputMode={props.digitos ? 'numeric' : undefined} style={st.input} />
    </div>
  );
  const rfc = (k, prefijo, estilo) => (
    <div style={{ ...st.field, ...estilo }}>
      <label style={st.label}>RFC</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={o[k] ?? ''} maxLength={13} onChange={e => set(k, e.target.value.toUpperCase().replace(/\s/g, ''))} style={{ ...st.input, flex: 1, minWidth: 0 }} />
        <button type="button" onClick={() => calcular(prefijo)} style={{ ...st.saveBtn, marginTop: 0, padding: '0 12px', fontSize: 13 }}>Calcular</button>
      </div>
    </div>
  );
  const persona = (pre) => (
    <>
      <div style={cols(3)}>
        {campo(pre + 'nombres', 'Nombre(s)', { max: 100 })}
        {campo(pre + 'apellido_paterno', 'Apellido Paterno', { max: 100 })}
        {campo(pre + 'apellido_materno', 'Apellido Materno', { max: 100 })}
      </div>
    </>
  );
  const aviso = { backgroundColor: '#FFF7E6', border: '1px solid #F5C26B', color: '#7A4B00', padding: '12px 16px', borderRadius: 8, fontSize: 13, marginBottom: 16 };
  const sub = { fontSize: 15, fontWeight: '600', color: '#374151', margin: '16px 0 8px' };

  return (
    <div style={st.card}>
      <div style={st.title}>Obligado Solidario</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', marginBottom: o.incluye ? 16 : 0 }}>
        <span style={{ fontSize: 14, fontWeight: '600', color: '#374151' }}>¿Se incluye un obligado solidario?</span>
        {[[true, 'Sí'], [false, 'No']].map(([v, t]) => (
          <label key={t} style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="radio" name="banregio-obligado-incluye" checked={(o.incluye === true) === v} onChange={() => set('incluye', v)} /> {t}
          </label>
        ))}
      </div>
      {!o.incluye && o.nombres && (
        <p style={{ fontSize: 13, color: '#6b7280', margin: '12px 0 0' }}>Los datos capturados del obligado se conservan; no se imprimen mientras la respuesta sea No.</p>
      )}
      {o.incluye && (
        <>
          {avisos.length > 0 && <div style={aviso}>{avisos.map(a => <div key={a}>{a}</div>)}</div>}
          {persona('')}
          <div style={fila}>
            {campo('fecha_nacimiento', 'Fecha de Nacimiento', { type: 'date', estilo: ancho(3, 40, ANCHO_MIN.fecha) })}
            {rfc('rfc', '', ancho(3, 60, ANCHO_MIN.rfc))}
            <div style={{ ...st.field, ...ancho(3, 60, ANCHO_MIN.estadoCivil) }}>
              <label style={st.label}>Estado Civil</label>
              <select value={o.estado_civil_id ?? ''} onChange={e => set('estado_civil_id', e.target.value === '' ? null : Number(e.target.value))} style={st.input}>
                <option value="">— Selecciona —</option>
                {estadosCiviles.map(x => <option key={x.id} value={x.id}>{x.nombre}</option>)}
              </select>
            </div>
          </div>
          <div style={sub}>Domicilio</div>
          <div style={fila}>
            {campo('calle', 'Calle', { max: 200, estilo: ancho(4, 150, ANCHO_MIN.calle) })}
            {campo('no_ext', 'No. Ext.', { max: 50, estilo: ancho(4, 30, ANCHO_MIN.numero) })}
            {campo('no_int', 'No. Int.', { max: 50, estilo: ancho(4, 30, ANCHO_MIN.numero) })}
            <div style={{ ...st.field, ...ancho(4, 50, ANCHO_MIN.cp) }}>
              <label style={st.label}>Código Postal</label>
              <input value={o.codigo_postal || ''} maxLength={5} inputMode="numeric"
                onChange={e => { const cp = e.target.value.replace(/\D/g, ''); set('codigo_postal', cp); if (cp.length === 5) buscarCp(cp); }} style={st.input} />
              {avisoCp && <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>{avisoCp}</div>}
            </div>
          </div>
          {/* Colonia 140% y Teléfono 50% del ancho de columna que tenían; Ciudad y Entidad igual (Marco, 2026-10-28) */}
          <div style={fila}>
            <div style={{ ...st.field, ...ancho(4, 140, 0) }}>
              <label style={st.label}>Colonia</label>
              <select value={o.colonia || ''} onChange={e => set('colonia', e.target.value || null)} style={st.input} disabled={!colonias.length}>
                <option value="">{colonias.length ? '— Selecciona —' : 'Captura el código postal'}</option>
                {[...new Set(colonias.map(x => x.colonia))].map(x => <option key={x} value={x}>{x}</option>)}
                {o.colonia && !colonias.some(x => x.colonia === o.colonia) && <option value={o.colonia}>{o.colonia}</option>}
              </select>
            </div>
            <div style={{ ...st.field, ...ancho(4, 100, 0) }}><label style={st.label}>Ciudad</label><input readOnly value={o.ciudad || ''} style={soloLectura} /></div>
            <div style={{ ...st.field, ...ancho(4, 100, 0) }}><label style={st.label}>Entidad Federativa</label><input readOnly value={o.entidad_federativa || ''} style={soloLectura} /></div>
            {campo('telefono_celular', 'Teléfono Celular', { max: 10, digitos: true, estilo: ancho(4, 50, ANCHO_MIN.telefono) })}
          </div>
          {esSociedadConyugal && (
            <>
              <div style={sub}>Cónyuge del Obligado Solidario</div>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>El domicilio del cónyuge se toma del obligado solidario.</p>
              {persona('conyuge_')}
              <div style={fila}>
                {campo('conyuge_fecha_nacimiento', 'Fecha de Nacimiento', { type: 'date', estilo: ancho(3, 40, ANCHO_MIN.fecha) })}
                {rfc('conyuge_rfc', 'conyuge_', ancho(3, 60, ANCHO_MIN.rfc))}
                {campo('conyuge_telefono_celular', 'Teléfono Celular', { max: 10, digitos: true, estilo: ancho(3, 50, ANCHO_MIN.telefono) })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
